import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { categoriesService, type CategoryCreatePayload } from '@/services/inventory';
import type { Category, ItemAttributeDef } from '@/types/inventory';
import { CategoryImageField } from './CategoryImageField';
import { CategoryAttributeTemplateEditor } from './CategoryAttributeTemplateEditor';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // When supplied, the sheet edits this category; otherwise it creates a new one.
  category?: Category | null;
}

const NONE = '__none';

export function CategoryFormSheet({ open, onOpenChange, category }: Props) {
  const qc = useQueryClient();
  const isEditing = !!category;

  const [form, setForm] = useState<CategoryCreatePayload>({
    code: '',
    name: '',
    description: '',
    parentId: undefined,
    categoryKind: 'SELLABLE',
  });
  // Image URLs are owned here so previews update immediately after upload/clear.
  const [tileUrl, setTileUrl] = useState<string | null>(null);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  // ADR-040 — the category's typed item-attribute schema (edited separately
  // from `form` so the editor owns the array; variantAxes are preserved as-is).
  const [attrs, setAttrs] = useState<ItemAttributeDef[]>([]);

  useEffect(() => {
    if (open) {
      setForm({
        code: category?.code ?? '',
        name: category?.name ?? '',
        description: category?.description ?? '',
        parentId: category?.parentId ?? undefined,
        categoryKind: category?.categoryKind ?? 'SELLABLE',
      });
      setTileUrl(category?.imageUrl ?? null);
      setBannerUrl(category?.bannerUrl ?? null);
      setAttrs(category?.attributeTemplate?.itemAttributes ?? []);
    }
  }, [open, category]);

  const onImageChanged = (c: Category) => {
    setTileUrl(c.imageUrl ?? null);
    setBannerUrl(c.bannerUrl ?? null);
  };

  // Load existing categories so the parent dropdown can pick from them.
  const categoriesQuery = useQuery({
    queryKey: ['inventory', 'categories'],
    queryFn: () => categoriesService.list(),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: async (payload: CategoryCreatePayload) => {
      if (isEditing && category) {
        // Category code is immutable post-create (the BE's UpdateDto
        // doesn't declare `code` and the global ValidationPipe runs
        // forbidNonWhitelisted, so sending it would trip a confusing
        // "property code should not exist" error).
        const { code: _code, ...patch } = payload;
        return categoriesService.update(category.id, patch);
      }
      return categoriesService.create(payload);
    },
    onSuccess: () => {
      toast.success(isEditing ? 'Category updated' : 'Category created');
      qc.invalidateQueries({ queryKey: ['inventory', 'categories'] });
      onOpenChange(false);
    },
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Drop rows with a blank name; keep existing variantAxes untouched.
    const itemAttributes = attrs
      .map((a) => ({ ...a, name: a.name.trim() }))
      .filter((a) => a.name.length > 0);
    const variantAxes = category?.attributeTemplate?.variantAxes;
    const hasTemplate = itemAttributes.length > 0 || (variantAxes && variantAxes.length > 0);
    mutation.mutate({
      ...form,
      description: form.description?.trim() || undefined,
      parentId: form.parentId || undefined,
      // null explicitly clears the template server-side; undefined leaves it.
      attributeTemplate: hasTemplate
        ? { itemAttributes, ...(variantAxes ? { variantAxes } : {}) }
        : isEditing
          ? null
          : undefined,
    });
  };

  const eligibleParents = (categoriesQuery.data ?? []).filter(
    (c) => c.id !== category?.id, // can't parent itself
  );

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? 'Edit category' : 'New category'}
      description={
        isEditing
          ? 'Update the category code, name, parent, or description.'
          : 'Categories are organisational tags for inventory items. They do not affect stock or pricing.'
      }
      submitLabel={isEditing ? 'Save changes' : 'Create category'}
      onSubmit={onSubmit}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      // Fixed (definite) height so the tall body — visibility, tile/banner
      // uploaders, description, attribute-template editor — scrolls internally
      // instead of clipping under the pinned footer on shorter viewports.
      fixedHeight="xl"
    >
      <FormField
        label="Code"
        htmlFor="code"
        required
        hint="Short uppercase identifier — e.g. SHIRTS, TSHIRTS, TROUSERS."
      >
        <Input
          id="code"
          value={form.code}
          onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
          placeholder="SHIRTS"
          required
          maxLength={32}
          className="font-mono"
        />
      </FormField>

      <FormField label="Name" htmlFor="name" required>
        <Input
          id="name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="Shirts"
          required
          maxLength={200}
        />
      </FormField>

      <FormField
        label="Parent category"
        htmlFor="parent"
        hint="Optional. Pick a parent to nest this category under it."
      >
        <Select
          value={form.parentId ?? NONE}
          onValueChange={(v) =>
            setForm((f) => ({ ...f, parentId: v === NONE ? undefined : v }))
          }
        >
          <SelectTrigger id="parent">
            <SelectValue placeholder="No parent (top-level)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>No parent (top-level)</SelectItem>
            {eligibleParents.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.code} · {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      <FormField
        label="Visibility"
        htmlFor="visibility"
        hint="Storefront categories appear to shoppers on the portal. Internal categories (raw materials, trims, packaging) stay operations-only."
      >
        <div className="grid grid-cols-2 gap-2" id="visibility">
          {(
            [
              { v: 'SELLABLE', title: 'Storefront', sub: 'Shown to shoppers' },
              { v: 'INTERNAL', title: 'Internal only', sub: 'Raw materials · ops-only' },
            ] as const
          ).map((opt) => {
            const active = (form.categoryKind ?? 'SELLABLE') === opt.v;
            return (
              <button
                key={opt.v}
                type="button"
                onClick={() => setForm((f) => ({ ...f, categoryKind: opt.v }))}
                className={
                  'rounded-md border px-3 py-2 text-left transition-colors ' +
                  (active
                    ? 'border-primary bg-primary/5 ring-1 ring-inset ring-primary/30'
                    : 'bg-card hover:bg-muted/50')
                }
              >
                <div className="text-[13px] font-medium text-foreground">{opt.title}</div>
                <div className="text-[11px] text-muted-foreground">{opt.sub}</div>
              </button>
            );
          })}
        </div>
      </FormField>

      {/* Images first (above the optional description) so the uploaders are
          always visible without scrolling to the very bottom of the dialog. */}
      {isEditing && category ? (
        <div className="space-y-2 border-t pt-4">
          <div className="text-[12px] font-medium text-foreground">Images</div>
          <div className="flex flex-wrap gap-4">
            <div className="space-y-1.5">
              <div className="text-[11.5px] text-muted-foreground">Tile</div>
              <CategoryImageField
                categoryId={category.id}
                kind="tile"
                currentUrl={tileUrl}
                onChanged={onImageChanged}
              />
            </div>
            <div className="min-w-[200px] flex-1 space-y-1.5">
              <div className="text-[11.5px] text-muted-foreground">Banner</div>
              <CategoryImageField
                categoryId={category.id}
                kind="banner"
                currentUrl={bannerUrl}
                onChanged={onImageChanged}
              />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Tile — square (≈ 800×800), shown next to the category name in storefront
            grids. Banner — 3:1 (≈ 1800×600), the full-width category-page hero.
          </p>
        </div>
      ) : (
        <div className="rounded-md border border-dashed bg-muted/30 px-3 py-3 text-[12px] text-muted-foreground">
          Save the category first — then you can upload a tile + banner image here.
        </div>
      )}

      <FormField label="Description" htmlFor="description">
        <Textarea
          id="description"
          value={form.description ?? ''}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          placeholder="Optional short description."
          rows={3}
          maxLength={500}
        />
      </FormField>

      <CategoryAttributeTemplateEditor value={attrs} onChange={setAttrs} />
    </FormSheet>
  );
}
